/**
 * UserManagement - Comprehensive user listing and management
 * Phase 11.5 - Admin Console
 */
import React, { useState, useCallback, useEffect } from 'react'
import {
  IoSearch,
  IoFilter,
  IoRefresh,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoDownload,
  IoPerson,
  IoChevronDown,
  IoChevronUp,
  IoEllipsisVertical,
  IoTrendingUp,
  IoWarning,
  IoTimeOutline,
  IoPricetag,
  IoCard,
  IoReceipt,
  IoReturnDownBack,
  IoOpenOutline,
  IoKey,
  IoLogOut,
  IoMail,
  IoBan,
} from 'react-icons/io5'
import { apiGetAuth, apiPutAuth, apiPostAuth } from '../../api'
import { AccordionSection } from './AccordionSection'
import { CompactCard, CompactCardGrid } from './CompactCard'

interface Subscription {
  plan: string
  status: string
  stripe_customer_id: string | null
  current_period_end: string | null
  cancelled_at: string | null
  cancel_at_period_end: boolean
}

interface UserListItem {
  id: number
  email: string
  clerk_id: string | null
  is_active: boolean
  role: string | null
  created_at: string
  last_active: string | null
  subscription: Subscription | null
  tags: string[]
  tracked_bets_count: number
  saved_builders_count: number
}

interface UserStats {
  total_users: number
  active_users: number
  inactive_users: number
  new_users_7d: number
  new_users_30d: number
  cancelled_subscriptions: number
  by_plan: Record<string, number>
  by_status: Record<string, number>
}

interface Filters {
  q: string
  plan: string
  status: string
  is_active: string
  is_cancelled: string
  sort_by: string
  sort_order: string
}

interface Invoice {
  id: string
  number: string | null
  status: string
  amount_due: number
  amount_paid: number
  currency: string
  created: string
  paid_at: string | null
  invoice_pdf: string | null
  hosted_invoice_url: string | null
}

interface Payment {
  id: string
  amount: number
  currency: string
  status: string
  created: string
  description: string | null
  latest_charge: string | null
  refunded: boolean
}

const PLANS = ['free', 'pro', 'premium', 'lifetime']
const STATUSES = ['active', 'cancelled', 'past_due', 'trialing', 'incomplete']

export function UserManagement() {
  const [users, setUsers] = useState<UserListItem[]>([])
  const [stats, setStats] = useState<UserStats | null>(null)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  // Billing state
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [payments, setPayments] = useState<Payment[]>([])
  const [billingLoading, setBillingLoading] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [refundPayment, setRefundPayment] = useState<Payment | null>(null)
  const [refundAmount, setRefundAmount] = useState('')
  const [refundReason, setRefundReason] = useState<'requested_by_customer' | 'duplicate' | 'fraudulent'>('requested_by_customer')

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')

  const LIMIT = 25

  const [filters, setFilters] = useState<Filters>({
    q: '',
    plan: '',
    status: '',
    is_active: '',
    is_cancelled: '',
    sort_by: 'created_at',
    sort_order: 'desc',
  })

  // Load users
  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.set('limit', String(LIMIT))
      params.set('offset', String(page * LIMIT))

      if (filters.q) params.set('q', filters.q)
      if (filters.plan) params.set('plan', filters.plan)
      if (filters.status) params.set('status', filters.status)
      if (filters.is_active) params.set('is_active', filters.is_active)
      if (filters.is_cancelled) params.set('is_cancelled', filters.is_cancelled)
      params.set('sort_by', filters.sort_by)
      params.set('sort_order', filters.sort_order)

      const response = await apiGetAuth<{
        total: number
        users: UserListItem[]
      }>(`/admin/users?${params.toString()}`)

      setUsers(response?.users || [])
      setTotal(response?.total || 0)
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      setLoading(false)
    }
  }, [page, filters])

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const response = await apiGetAuth<UserStats>('/admin/users/stats')
      setStats(response)
    } catch (error) {
      console.error('Error loading stats:', error)
    }
  }, [])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  // Reset page when filters change
  useEffect(() => {
    setPage(0)
  }, [filters])

  // Update filter
  const updateFilter = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  // Toggle sort
  const toggleSort = (field: string) => {
    if (filters.sort_by === field) {
      updateFilter('sort_order', filters.sort_order === 'asc' ? 'desc' : 'asc')
    } else {
      setFilters((prev) => ({ ...prev, sort_by: field, sort_order: 'desc' }))
    }
  }

  // Update subscription
  const updateSubscription = async (userId: number, plan: string) => {
    setActionLoading(true)
    try {
      await apiPutAuth(`/admin/users/${userId}/subscription`, { plan })
      loadUsers()
      loadStats()
    } catch (error) {
      console.error('Error updating subscription:', error)
    } finally {
      setActionLoading(false)
    }
  }

  // Toggle user active
  const toggleUserActive = async (userId: number) => {
    setActionLoading(true)
    try {
      await apiPostAuth(`/admin/users/${userId}/toggle-active`, {})
      loadUsers()
      loadStats()
    } catch (error) {
      console.error('Error toggling user:', error)
    } finally {
      setActionLoading(false)
    }
  }

  // Load billing data for selected user
  const loadBillingData = useCallback(async (userId: number) => {
    setBillingLoading(true)
    try {
      const [invoicesRes, paymentsRes] = await Promise.all([
        apiGetAuth<{ invoices: Invoice[] }>(`/admin/users/${userId}/billing/invoices?limit=10`),
        apiGetAuth<{ payments: Payment[] }>(`/admin/users/${userId}/billing/payments?limit=20`),
      ])
      setInvoices(invoicesRes?.invoices || [])
      setPayments(paymentsRes?.payments || [])
    } catch (error) {
      console.error('Error loading billing data:', error)
      setInvoices([])
      setPayments([])
    } finally {
      setBillingLoading(false)
    }
  }, [])

  // Open Stripe billing portal
  const openBillingPortal = async (userId: number) => {
    setActionLoading(true)
    try {
      const response = await apiGetAuth<{ url: string }>(`/admin/users/${userId}/billing/portal`)
      if (response?.url) {
        window.open(response.url, '_blank')
      }
    } catch (error) {
      console.error('Error opening billing portal:', error)
      alert('Failed to open billing portal. User may not have a Stripe customer ID.')
    } finally {
      setActionLoading(false)
    }
  }

  // Process refund
  const processRefund = async () => {
    if (!selectedUser || !refundPayment) return
    setActionLoading(true)
    try {
      const amountCents = refundAmount ? Math.round(parseFloat(refundAmount) * 100) : undefined
      await apiPostAuth(`/admin/users/${selectedUser.id}/billing/refund`, {
        payment_intent_id: refundPayment.id,
        amount_cents: amountCents,
        reason: refundReason,
      })
      alert('Refund processed successfully')
      setShowRefundModal(false)
      setRefundPayment(null)
      setRefundAmount('')
      loadBillingData(selectedUser.id)
    } catch (error) {
      console.error('Error processing refund:', error)
      alert('Failed to process refund. Check console for details.')
    } finally {
      setActionLoading(false)
    }
  }

  // Load billing when user selected
  useEffect(() => {
    if (selectedUser?.subscription?.stripe_customer_id) {
      loadBillingData(selectedUser.id)
    } else {
      setInvoices([])
      setPayments([])
    }
  }, [selectedUser, loadBillingData])

  // Trigger password reset
  const triggerPasswordReset = async (userId: number) => {
    setActionLoading(true)
    try {
      const response = await apiPostAuth<{ message: string; email: string; clerk_dashboard_url: string }>(
        `/admin/users/${userId}/auth/password-reset`,
        {}
      )
      alert(response?.message || 'Password reset triggered')
    } catch (error) {
      console.error('Error triggering password reset:', error)
      alert('Failed to trigger password reset. User may not be linked to Clerk.')
    } finally {
      setActionLoading(false)
    }
  }

  // Revoke all sessions
  const revokeAllSessions = async (userId: number) => {
    if (!confirm('This will sign out the user from all devices. Continue?')) return
    setActionLoading(true)
    try {
      const response = await apiPostAuth<{ message: string; revoked_sessions: number }>(
        `/admin/users/${userId}/auth/revoke-sessions`,
        {}
      )
      alert(response?.message || `Revoked ${response?.revoked_sessions || 0} sessions`)
    } catch (error) {
      console.error('Error revoking sessions:', error)
      alert('Failed to revoke sessions. User may not be linked to Clerk.')
    } finally {
      setActionLoading(false)
    }
  }

  // Ban/Unban user
  const toggleBanUser = async (userId: number, currentlyActive: boolean) => {
    const action = currentlyActive ? 'ban' : 'unban'
    if (!confirm(`Are you sure you want to ${action} this user?`)) return
    setActionLoading(true)
    try {
      await apiPostAuth(`/admin/users/${userId}/auth/${action}`, {})
      alert(`User has been ${action}ned`)
      loadUsers()
      loadStats()
      if (selectedUser?.id === userId) {
        setSelectedUser({ ...selectedUser, is_active: !currentlyActive })
      }
    } catch (error) {
      console.error(`Error ${action}ning user:`, error)
      alert(`Failed to ${action} user.`)
    } finally {
      setActionLoading(false)
    }
  }

  // Send email to user
  const sendEmailToUser = async () => {
    if (!selectedUser || !emailSubject.trim() || !emailMessage.trim()) return
    setActionLoading(true)
    try {
      await apiPostAuth(`/admin/users/${selectedUser.id}/email`, {
        subject: emailSubject,
        message: emailMessage,
      })
      alert('Email sent successfully')
      setShowEmailModal(false)
      setEmailSubject('')
      setEmailMessage('')
    } catch (error) {
      console.error('Error sending email:', error)
      alert('Failed to send email. Email service may not be configured.')
    } finally {
      setActionLoading(false)
    }
  }

  // Export CSV
  const exportCSV = async () => {
    try {
      const params = new URLSearchParams()
      if (filters.plan) params.set('plan', filters.plan)
      if (filters.status) params.set('status', filters.status)
      if (filters.is_cancelled) params.set('is_cancelled', filters.is_cancelled)

      const response = await fetch(`/api/v1/admin/users/export/csv?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Error exporting CSV:', error)
    }
  }

  // Get plan badge color
  const getPlanColor = (plan: string): string => {
    switch (plan) {
      case 'lifetime':
        return 'plan-lifetime'
      case 'premium':
        return 'plan-premium'
      case 'pro':
        return 'plan-pro'
      default:
        return 'plan-free'
    }
  }

  // Get status badge color
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return 'status-active'
      case 'cancelled':
        return 'status-cancelled'
      case 'past_due':
        return 'status-past-due'
      case 'trialing':
        return 'status-trialing'
      default:
        return 'status-none'
    }
  }

  const totalPages = Math.ceil(total / LIMIT)

  return (
    <div className="user-management">
      {/* Stats Overview */}
      <CompactCardGrid columns={4}>
        <CompactCard
          label="Total Users"
          value={stats?.total_users ?? '—'}
          icon={<IoPerson size={20} />}
          status="neutral"
        />
        <CompactCard
          label="Active Users"
          value={stats?.active_users ?? '—'}
          icon={<IoCheckmarkCircle size={20} />}
          status="healthy"
        />
        <CompactCard
          label="New (7d)"
          value={stats?.new_users_7d ?? '—'}
          icon={<IoTrendingUp size={20} />}
          status="neutral"
          trend={stats?.new_users_30d ? `${stats.new_users_30d} in 30d` : undefined}
        />
        <CompactCard
          label="Cancelled"
          value={stats?.cancelled_subscriptions ?? '—'}
          icon={<IoWarning size={20} />}
          status={stats?.cancelled_subscriptions && stats.cancelled_subscriptions > 0 ? 'warning' : 'neutral'}
        />
      </CompactCardGrid>

      {/* Plan Breakdown */}
      {stats?.by_plan && (
        <div className="user-plan-breakdown">
          {PLANS.map((plan) => (
            <div key={plan} className={`plan-stat ${getPlanColor(plan)}`}>
              <span className="plan-count">{stats.by_plan[plan] || 0}</span>
              <span className="plan-label">{plan}</span>
            </div>
          ))}
        </div>
      )}

      {/* Search & Filters */}
      <AccordionSection title="User List" badge={total} defaultOpen={true}>
        <div className="user-list-header">
          <div className="user-search-bar">
            <IoSearch size={18} />
            <input
              type="text"
              placeholder="Search by email or ID..."
              value={filters.q}
              onChange={(e) => updateFilter('q', e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadUsers()}
            />
            <button className="search-btn" onClick={loadUsers}>
              Search
            </button>
          </div>

          <div className="user-list-actions">
            <button
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <IoFilter size={16} />
              Filters
            </button>
            <button className="export-btn" onClick={exportCSV}>
              <IoDownload size={16} />
              Export CSV
            </button>
            <button className="refresh-btn" onClick={loadUsers} disabled={loading}>
              <IoRefresh size={16} className={loading ? 'spinning' : ''} />
            </button>
          </div>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="user-filters">
            <div className="filter-group">
              <label>Plan</label>
              <select value={filters.plan} onChange={(e) => updateFilter('plan', e.target.value)}>
                <option value="">All Plans</option>
                {PLANS.map((p) => (
                  <option key={p} value={p}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select value={filters.status} onChange={(e) => updateFilter('status', e.target.value)}>
                <option value="">All Statuses</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Account Status</label>
              <select
                value={filters.is_active}
                onChange={(e) => updateFilter('is_active', e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Active Only</option>
                <option value="false">Inactive Only</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Cancelled</label>
              <select
                value={filters.is_cancelled}
                onChange={(e) => updateFilter('is_cancelled', e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Cancelled Only</option>
                <option value="false">Not Cancelled</option>
              </select>
            </div>

            <button className="clear-filters-btn" onClick={() => setFilters({
              q: '',
              plan: '',
              status: '',
              is_active: '',
              is_cancelled: '',
              sort_by: 'created_at',
              sort_order: 'desc',
            })}>
              Clear Filters
            </button>
          </div>
        )}

        {/* User Table */}
        <div className="user-table">
          <div className="user-table-header">
            <span className="col-user" onClick={() => toggleSort('email')}>
              User
              {filters.sort_by === 'email' && (
                filters.sort_order === 'asc' ? <IoChevronUp size={12} /> : <IoChevronDown size={12} />
              )}
            </span>
            <span className="col-plan" onClick={() => toggleSort('plan')}>
              Plan
              {filters.sort_by === 'plan' && (
                filters.sort_order === 'asc' ? <IoChevronUp size={12} /> : <IoChevronDown size={12} />
              )}
            </span>
            <span className="col-status">Status</span>
            <span className="col-activity">Activity</span>
            <span className="col-created" onClick={() => toggleSort('created_at')}>
              Created
              {filters.sort_by === 'created_at' && (
                filters.sort_order === 'asc' ? <IoChevronUp size={12} /> : <IoChevronDown size={12} />
              )}
            </span>
            <span className="col-actions">Actions</span>
          </div>

          {loading && users.length === 0 ? (
            <div className="user-table-loading">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="user-table-empty">No users found matching your filters.</div>
          ) : (
            users.map((user) => (
              <div
                key={user.id}
                className={`user-table-row ${!user.is_active ? 'inactive' : ''} ${
                  selectedUser?.id === user.id ? 'selected' : ''
                }`}
                onClick={() => setSelectedUser(selectedUser?.id === user.id ? null : user)}
              >
                <span className="col-user">
                  <span className="user-email">{user.email}</span>
                  <span className="user-id">ID: {user.id}</span>
                  {user.tags.length > 0 && (
                    <span className="user-tags">
                      {user.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="user-tag">
                          <IoPricetag size={10} />
                          {tag}
                        </span>
                      ))}
                      {user.tags.length > 2 && <span className="user-tag-more">+{user.tags.length - 2}</span>}
                    </span>
                  )}
                </span>

                <span className="col-plan">
                  <span className={`plan-badge ${getPlanColor(user.subscription?.plan || 'free')}`}>
                    {user.subscription?.plan || 'free'}
                  </span>
                </span>

                <span className="col-status">
                  <span className={`status-badge ${getStatusColor(user.subscription?.status || 'none')}`}>
                    {user.subscription?.status || 'none'}
                  </span>
                  {user.subscription?.cancel_at_period_end && (
                    <span className="cancelling-badge">Cancelling</span>
                  )}
                  {user.subscription?.cancelled_at && (
                    <span className="cancelled-date">
                      {new Date(user.subscription.cancelled_at).toLocaleDateString()}
                    </span>
                  )}
                </span>

                <span className="col-activity">
                  <span className="activity-stats">
                    <span title="Tracked Bets">{user.tracked_bets_count} bets</span>
                    <span title="Saved Builders">{user.saved_builders_count} builders</span>
                  </span>
                  {user.last_active && (
                    <span className="last-active">
                      <IoTimeOutline size={12} />
                      {new Date(user.last_active).toLocaleDateString()}
                    </span>
                  )}
                </span>

                <span className="col-created">
                  {new Date(user.created_at).toLocaleDateString()}
                </span>

                <span className="col-actions" onClick={(e) => e.stopPropagation()}>
                  <div className="action-dropdown">
                    <button className="action-trigger">
                      <IoEllipsisVertical size={16} />
                    </button>
                    <div className="action-menu">
                      <button onClick={() => toggleUserActive(user.id)} disabled={actionLoading}>
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <div className="action-divider" />
                      <span className="action-label">Change Plan:</span>
                      {PLANS.map((plan) => (
                        <button
                          key={plan}
                          onClick={() => updateSubscription(user.id, plan)}
                          disabled={actionLoading || user.subscription?.plan === plan}
                          className={user.subscription?.plan === plan ? 'current' : ''}
                        >
                          {plan.charAt(0).toUpperCase() + plan.slice(1)}
                          {user.subscription?.plan === plan && ' (current)'}
                        </button>
                      ))}
                    </div>
                  </div>
                </span>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="user-pagination">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              Previous
            </button>
            <span>
              Page {page + 1} of {totalPages} ({total} users)
            </span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        )}
      </AccordionSection>

      {/* Selected User Detail */}
      {selectedUser && (
        <AccordionSection title={`User Details: ${selectedUser.email}`} defaultOpen={true}>
          <div className="user-detail-panel">
            <div className="user-detail-grid">
              <div className="detail-item">
                <label>User ID</label>
                <span>{selectedUser.id}</span>
              </div>
              <div className="detail-item">
                <label>Clerk ID</label>
                <span>{selectedUser.clerk_id || '—'}</span>
              </div>
              <div className="detail-item">
                <label>Email</label>
                <span>{selectedUser.email}</span>
              </div>
              <div className="detail-item">
                <label>Role</label>
                <span>{selectedUser.role || 'user'}</span>
              </div>
              <div className="detail-item">
                <label>Account Status</label>
                <span className={selectedUser.is_active ? 'active' : 'inactive'}>
                  {selectedUser.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="detail-item">
                <label>Created</label>
                <span>{new Date(selectedUser.created_at).toLocaleString()}</span>
              </div>
            </div>

            {/* User Actions */}
            {selectedUser.clerk_id && (
              <div className="user-auth-actions">
                <h4>
                  <IoKey size={16} />
                  Account Actions
                </h4>
                <div className="auth-actions-grid">
                  <button
                    className="auth-action-btn"
                    onClick={() => triggerPasswordReset(selectedUser.id)}
                    disabled={actionLoading}
                  >
                    <IoKey size={14} />
                    Password Reset
                  </button>
                  <button
                    className="auth-action-btn"
                    onClick={() => revokeAllSessions(selectedUser.id)}
                    disabled={actionLoading}
                  >
                    <IoLogOut size={14} />
                    Revoke Sessions
                  </button>
                  <button
                    className={`auth-action-btn ${selectedUser.is_active ? 'danger' : 'success'}`}
                    onClick={() => toggleBanUser(selectedUser.id, selectedUser.is_active)}
                    disabled={actionLoading}
                  >
                    <IoBan size={14} />
                    {selectedUser.is_active ? 'Ban User' : 'Unban User'}
                  </button>
                  <button
                    className="auth-action-btn email"
                    onClick={() => setShowEmailModal(true)}
                    disabled={actionLoading}
                  >
                    <IoMail size={14} />
                    Email User
                  </button>
                </div>
              </div>
            )}

            {selectedUser.subscription && (
              <div className="user-subscription-detail">
                <h4>Subscription Details</h4>
                <div className="user-detail-grid">
                  <div className="detail-item">
                    <label>Plan</label>
                    <span className={`plan-badge ${getPlanColor(selectedUser.subscription.plan)}`}>
                      {selectedUser.subscription.plan}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <span className={`status-badge ${getStatusColor(selectedUser.subscription.status)}`}>
                      {selectedUser.subscription.status}
                    </span>
                  </div>
                  {selectedUser.subscription.current_period_end && (
                    <div className="detail-item">
                      <label>Period End</label>
                      <span>{new Date(selectedUser.subscription.current_period_end).toLocaleDateString()}</span>
                    </div>
                  )}
                  {selectedUser.subscription.stripe_customer_id && (
                    <div className="detail-item">
                      <label>Stripe Customer</label>
                      <span className="stripe-id">{selectedUser.subscription.stripe_customer_id}</span>
                    </div>
                  )}
                  {selectedUser.subscription.cancelled_at && (
                    <div className="detail-item">
                      <label>Cancelled At</label>
                      <span className="cancelled">
                        {new Date(selectedUser.subscription.cancelled_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {selectedUser.subscription.cancel_at_period_end && (
                    <div className="detail-item full-width">
                      <span className="cancelling-notice">
                        <IoWarning size={14} />
                        Subscription will cancel at end of billing period
                      </span>
                    </div>
                  )}
                </div>

                {/* Billing Actions */}
                {selectedUser.subscription.stripe_customer_id && (
                  <div className="billing-actions">
                    <button
                      className="billing-action-btn"
                      onClick={() => openBillingPortal(selectedUser.id)}
                      disabled={actionLoading}
                    >
                      <IoOpenOutline size={14} />
                      Open Billing Portal
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Billing History */}
            {selectedUser.subscription?.stripe_customer_id && (
              <div className="user-billing-history">
                <h4>
                  <IoReceipt size={16} />
                  Billing History
                </h4>
                {billingLoading ? (
                  <p className="billing-loading">Loading billing data...</p>
                ) : (
                  <>
                    {/* Invoices */}
                    {invoices.length > 0 && (
                      <div className="invoices-section">
                        <h5>Recent Invoices</h5>
                        <div className="invoices-list">
                          {invoices.map((inv) => (
                            <div key={inv.id} className={`invoice-item ${inv.status}`}>
                              <span className="invoice-number">{inv.number || inv.id.slice(-8)}</span>
                              <span className="invoice-amount">
                                {(inv.amount_paid / 100).toFixed(2)} {inv.currency.toUpperCase()}
                              </span>
                              <span className={`invoice-status status-${inv.status}`}>{inv.status}</span>
                              <span className="invoice-date">{new Date(inv.created).toLocaleDateString()}</span>
                              {inv.invoice_pdf && (
                                <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="invoice-pdf">
                                  <IoDownload size={14} />
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Payments with Refund */}
                    {payments.length > 0 && (
                      <div className="payments-section">
                        <h5>Payment History</h5>
                        <div className="payments-list">
                          {payments.map((pmt) => (
                            <div key={pmt.id} className={`payment-item ${pmt.status} ${pmt.refunded ? 'refunded' : ''}`}>
                              <span className="payment-amount">
                                {(pmt.amount / 100).toFixed(2)} {pmt.currency.toUpperCase()}
                              </span>
                              <span className={`payment-status status-${pmt.status}`}>
                                {pmt.refunded ? 'Refunded' : pmt.status}
                              </span>
                              <span className="payment-date">{new Date(pmt.created).toLocaleDateString()}</span>
                              {!pmt.refunded && pmt.status === 'succeeded' && (
                                <button
                                  className="refund-btn"
                                  onClick={() => {
                                    setRefundPayment(pmt)
                                    setShowRefundModal(true)
                                  }}
                                >
                                  <IoReturnDownBack size={14} />
                                  Refund
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {invoices.length === 0 && payments.length === 0 && (
                      <p className="no-billing-data">No billing history found.</p>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="user-usage-stats">
              <h4>Usage Statistics</h4>
              <div className="usage-grid">
                <div className="usage-stat">
                  <span className="usage-value">{selectedUser.tracked_bets_count}</span>
                  <span className="usage-label">Tracked Bets</span>
                </div>
                <div className="usage-stat">
                  <span className="usage-value">{selectedUser.saved_builders_count}</span>
                  <span className="usage-label">Saved Builders</span>
                </div>
              </div>
            </div>

            {selectedUser.tags.length > 0 && (
              <div className="user-tags-detail">
                <h4>Tags</h4>
                <div className="tags-list">
                  {selectedUser.tags.map((tag) => (
                    <span key={tag} className="tag-badge">
                      <IoPricetag size={12} />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </AccordionSection>
      )}

      {/* Refund Modal */}
      {showRefundModal && refundPayment && (
        <div className="refund-modal-overlay" onClick={() => setShowRefundModal(false)}>
          <div className="refund-modal" onClick={(e) => e.stopPropagation()}>
            <h4>
              <IoReturnDownBack size={18} />
              Process Refund
            </h4>
            <div className="refund-details">
              <p>
                Original payment: <strong>{(refundPayment.amount / 100).toFixed(2)} {refundPayment.currency.toUpperCase()}</strong>
              </p>
              <p>Date: {new Date(refundPayment.created).toLocaleString()}</p>
            </div>
            <div className="refund-form">
              <label>
                Refund Amount (leave empty for full refund):
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={refundPayment.amount / 100}
                  placeholder={`Max: ${(refundPayment.amount / 100).toFixed(2)}`}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                />
              </label>
              <label>
                Reason:
                <select value={refundReason} onChange={(e) => setRefundReason(e.target.value as typeof refundReason)}>
                  <option value="requested_by_customer">Requested by customer</option>
                  <option value="duplicate">Duplicate payment</option>
                  <option value="fraudulent">Fraudulent</option>
                </select>
              </label>
            </div>
            <div className="refund-actions">
              <button className="cancel-btn" onClick={() => setShowRefundModal(false)}>
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={processRefund}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Process Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && selectedUser && (
        <div className="email-modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="email-modal" onClick={(e) => e.stopPropagation()}>
            <h4>
              <IoMail size={18} />
              Email User
            </h4>
            <p className="email-recipient">
              To: <strong>{selectedUser.email}</strong>
            </p>
            <div className="email-form">
              <label>
                Subject:
                <input
                  type="text"
                  placeholder="Enter email subject..."
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  maxLength={200}
                />
              </label>
              <label>
                Message:
                <textarea
                  placeholder="Enter your message..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={8}
                  maxLength={10000}
                />
              </label>
            </div>
            <div className="email-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowEmailModal(false)
                  setEmailSubject('')
                  setEmailMessage('')
                }}
              >
                Cancel
              </button>
              <button
                className="send-btn"
                onClick={sendEmailToUser}
                disabled={actionLoading || !emailSubject.trim() || !emailMessage.trim()}
              >
                {actionLoading ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
